import { useMemo, useState, type FormEvent } from 'react'
import type { FieldSchema, RecordRow, TableSchema } from '../types'
import { enumLabels, label } from '../lib/labels'
import { Button, Notice } from './ui'

function normalized(spec: FieldSchema) {
  if (spec.anyOf) {
    const nonNull = spec.anyOf.find(v => v.type !== 'null') || spec.anyOf[0]
    return { ...nonNull, nullable: spec.anyOf.some(v => v.type === 'null') }
  }
  return { ...spec, nullable: false }
}

function coerce(raw: any, spec: FieldSchema) {
  const s = normalized(spec)
  if ((raw === '' || raw === undefined) && s.nullable) return null
  if (s.type === 'number' || s.type === 'integer') {
    if (raw === '') return null
    const n = Number(raw)
    return Number.isFinite(n) ? (s.type === 'integer' ? Math.trunc(n) : n) : raw
  }
  if (s.type === 'boolean') return raw === true || raw === 'true'
  return raw === '' && s.nullable ? null : raw
}

export function SchemaForm({ table, schema, initial, onSubmit, onCancel, submitting = false }: {
  table: string
  schema: TableSchema
  initial?: RecordRow | null
  onSubmit: (payload: RecordRow) => Promise<void> | void
  onCancel?: () => void
  submitting?: boolean
}) {
  const [values, setValues] = useState<RecordRow>(() => {
    const out: RecordRow = {}
    for (const [name, spec] of Object.entries(schema.json_schema.properties)) {
      out[name] = initial?.[name] ?? spec.default ?? ''
    }
    return out
  })
  const [touched, setTouched] = useState<Record<string, boolean>>({})
  const required = useMemo(() => new Set(schema.json_schema.required || []), [schema])

  const errors = useMemo(() => {
    const out: Record<string, string> = {}
    for (const [name, spec] of Object.entries(schema.json_schema.properties)) {
      const s = normalized(spec)
      const v = values[name]
      if (required.has(name) && (v === '' || v === null || v === undefined)) out[name] = 'Обязательное поле'
      if (v !== '' && v !== null && v !== undefined && (s.type === 'number' || s.type === 'integer') && !Number.isFinite(Number(v))) out[name] = 'Нужно число'
    }
    return out
  }, [schema, values, required])

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    setTouched(Object.fromEntries(Object.keys(schema.json_schema.properties).map(k => [k, true])))
    if (Object.keys(errors).length) return
    const payload: RecordRow = {}
    for (const [name, spec] of Object.entries(schema.json_schema.properties)) payload[name] = coerce(values[name], spec)
    await onSubmit(payload)
  }

  return <form className="schema-form" onSubmit={submit}>
    <Notice tone="info"><b>NULL ≠ 0.</b> Неизвестное или не указанное значение оставляйте пустым — не подменяйте его нулём.</Notice>
    <div className="form-grid">
      {Object.entries(schema.json_schema.properties).map(([name, spec]) => <Field key={name} name={name} spec={spec} required={required.has(name)} value={values[name]} error={touched[name] ? errors[name] : undefined} onChange={v => setValues(x => ({ ...x, [name]: v }))} onBlur={() => setTouched(x => ({ ...x, [name]: true }))} locked={Boolean(initial && schema.primary_key.includes(name))}/>) }
    </div>
    <div className="form-footer">
      {Object.keys(errors).length > 0 && Object.values(touched).some(Boolean) ? <span className="form-summary">Заполните обязательные поля: {Object.keys(errors).map(label).join(', ')}</span> : <span/>}
      <div className="form-actions">{onCancel && <Button type="button" onClick={onCancel}>Отмена</Button>}<Button type="submit" variant="primary" disabled={submitting}>{submitting ? 'Сохраняем…' : 'Сохранить'}</Button></div>
    </div>
  </form>
}

function Field({ name, spec, required, value, error, onChange, onBlur, locked }: { name: string; spec: FieldSchema; required: boolean; value: any; error?: string; onChange: (v:any)=>void; onBlur:()=>void; locked:boolean }) {
  const s = normalized(spec)
  const enums = s.enum || []
  const isTextArea = ['notes','known_data'].includes(name)
  const cls = `field ${error ? 'field-error' : ''}`
  return <label className={cls}>
    <span className="field-label">{label(name)}{required && <em>*</em>}</span>
    {enums.length ? <select value={value ?? ''} disabled={locked} onChange={e=>onChange(e.target.value)} onBlur={onBlur}>
      {s.nullable && <option value="">Не указано</option>}{enums.map(v=><option key={String(v)} value={String(v)}>{enumLabels[String(v)] || String(v)}</option>)}
    </select> : s.type === 'boolean' ? <div className="toggle-row">
      {s.nullable && <button type="button" className={`choice ${value===''||value==null?'active':''}`} onClick={()=>onChange('')} disabled={locked}>Неизвестно</button>}
      <button type="button" className={`choice ${value===true||value==='true'?'active':''}`} onClick={()=>onChange(true)} disabled={locked}>Да</button>
      <button type="button" className={`choice ${value===false||value==='false'?'active':''}`} onClick={()=>onChange(false)} disabled={locked}>Нет</button>
    </div> : isTextArea ? <textarea value={value ?? ''} rows={3} disabled={locked} onChange={e=>onChange(e.target.value)} onBlur={onBlur} placeholder={required?'Обязательное поле':'Не указано'}/>
    : <input type={s.type==='number'||s.type==='integer'?'number':'text'} step={s.type==='integer'?'1':'any'} value={value ?? ''} disabled={locked} onChange={e=>onChange(e.target.value)} onBlur={onBlur} placeholder={required?'Обязательное поле':'Не указано'}/>} 
    {error && <span className="field-message">{error}</span>}
  </label>
}
